import type { ItemDependencyState, ItemHealth } from '@/common/api/types/dashboard/health';
import {
  type DependencyImpactState,
  getPredecessorImpactState,
  getSuccessorImpactState,
  type PredecessorImpactOptions,
  type SuccessorImpactOptions,
} from '@/features/item-panel/components/tabs/dependencies/lib/dependencyImpact';

type DependencyEdge = ItemDependencyState['edges'][number];

const IMPACT_STATE_BY_EDGE_IMPACT: Record<DependencyEdge['impact'], DependencyImpactState> = {
  BLOCKED: 'error',
  AT_RISK: 'warning',
  CANNOT_EVALUATE: 'neutral',
  NO_ISSUE: 'none',
};

const getEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  direction: DependencyEdge['direction'],
  relatedItemId: number,
): DependencyImpactState => {
  const edge = currentItemHealth?.dependency?.edges.find(
    (candidate) => candidate.direction === direction && candidate.relatedItemId === relatedItemId,
  );
  return edge ? IMPACT_STATE_BY_EDGE_IMPACT[edge.impact] : 'none';
};

export const getPredecessorEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  predecessorId: number,
): DependencyImpactState => getEdgeImpactState(currentItemHealth, 'PREDECESSOR', predecessorId);

export const getSuccessorEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  successorId: number,
): DependencyImpactState => getEdgeImpactState(currentItemHealth, 'SUCCESSOR', successorId);

export const resolvePredecessorImpactState = (
  useEdges: boolean,
  options: PredecessorImpactOptions,
): DependencyImpactState =>
  useEdges
    ? getPredecessorEdgeImpactState(options.currentItemHealth, options.row.id)
    : getPredecessorImpactState(options);

export const resolveSuccessorImpactState = (
  useEdges: boolean,
  options: SuccessorImpactOptions & { currentItemHealth: ItemHealth | undefined },
): DependencyImpactState =>
  useEdges
    ? getSuccessorEdgeImpactState(options.currentItemHealth, options.row.id)
    : getSuccessorImpactState(options);
